import {
  Blueprint,
  BlueprintSource,
  BlueprintVisibility,
  Transaction,
  TransactionType,
} from 'types/rest/assetCatalogTypes';
import { GenericDsl } from 'types/rest/dslTypes';

declare const PRELOADED_BLAZE_USER_CONFIG: any;

export interface BlueprintTransactionDto {
  id: number;
  blueprintId?: number;
  name: string;
  description?: string;
  type: TransactionType;
  dsl: GenericDsl;
  sampleBody?: string;
  metadata?: unknown;
  sqlHint?: string;
  actionsData?: unknown[];
  createdBy?: string;
  updatedBy?: string;
}

export interface BlueprintDto {
  id: number;
  name: string;
  description?: string;
  tags?: string[];
  scope: 'GLOBAL' | 'ACCOUNT' | 'PRIVATE';
  transactionCount?: number;
  hasBlazeData?: boolean;
  hasImage?: boolean;
  imageBase64?: string;
  transactions?: BlueprintTransactionDto[];
  created?: number;
  updated?: number;
  createdDate?: string;
  updatedDate?: string;
  createdBy?: string;
  updatedBy?: string;
}

const apiBase = (): string => {
  const preload = typeof PRELOADED_BLAZE_USER_CONFIG !== 'undefined' ? PRELOADED_BLAZE_USER_CONFIG : null;
  const isPrivateCloud = !!(preload && preload.config && preload.config.isPrivateCloudRequest);
  return isPrivateCloud ? '/mock/api/v1' : '/api/v1';
};

export const toBlueprintTransaction = (dto: BlueprintTransactionDto): Transaction => ({
  id: dto.id,
  type: dto.type,
  name: dto.name,
  description: dto.description ?? '',
  dsl: dto.dsl,
  tags: [],
  priority: 0,
  sampleBody: dto.sampleBody,
  sqlHint: dto.sqlHint,
});

export type BlueprintScope = 'PRIVATE' | 'ACCOUNT' | 'GLOBAL';

export const visibilityToScope = (visibility: BlueprintVisibility): BlueprintScope =>
  visibility === 'PRIVATE' ? 'PRIVATE' : 'ACCOUNT';

/** Matches BlueprintBaseDto on the backend — used for both create and metadata-update. */
export interface BlueprintBaseDto {
  name: string;
  description: string;
  tags: string[];
  scope: BlueprintScope;
}

export const draftToBaseDto = (draft: {
  name: string;
  description: string;
  tags: string[];
  visibility: BlueprintVisibility;
}): BlueprintBaseDto => ({
  name: draft.name.trim(),
  description: draft.description.trim(),
  tags: draft.tags,
  scope: visibilityToScope(draft.visibility),
});

/** @deprecated use draftToBaseDto */
export const draftToCreateBody = draftToBaseDto;

export const toBlueprint = (dto: BlueprintDto): Blueprint => {
  const source: BlueprintSource = dto.scope === 'GLOBAL' ? 'BUILT_IN' : 'CUSTOM';
  const visibility: BlueprintVisibility = dto.scope === 'PRIVATE' ? 'PRIVATE' : 'ACCOUNT';
  return {
    id: String(dto.id),
    name: dto.name,
    description: dto.description ?? '',
    version: '',
    tags: dto.tags ?? [],
    thumbnail: dto.hasImage ? `${apiBase()}/blueprints/${dto.id}/image` : '',
    transactionCount: dto.transactionCount ?? (dto.transactions ? dto.transactions.length : 0),
    hasBlazeData: dto.hasBlazeData === true,
    transactions: dto.transactions ? dto.transactions.map(toBlueprintTransaction) : [],
    source,
    visibility,
    author: dto.updatedBy ?? dto.createdBy,
    createdAt: dto.created,
    updatedAt: dto.updated,
    createdDate: dto.createdDate,
    updatedDate: dto.updatedDate,
  };
};
